import {
  useNavigation,
  NavigationProp,
  useIsFocused,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect, useLayoutEffect } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { BaseNavigatorParamList } from "../../../../navigators/BaseNavigator/types";
import { ClubStackNavigatorParamList } from "../../../../navigators/ClubNavigator";
import { AnonymousMemberRedirectQuery } from "./__generated__/AnonymousMemberRedirectQuery.graphql";

export const AnonymousMemberRedirect = () => {
  const [queryRef, loadQuery] = useQueryLoader<AnonymousMemberRedirectQuery>(
    AnonymousMemberRedirectQueryString
  );
  const isFocused = useIsFocused();
  useEffect(() => {
    if (isFocused) {
      loadQuery({});
    }
  }, [isFocused, loadQuery]);
  return (
    <Suspense>
      {queryRef && isFocused ? (
        <AnonymousMemberRedirectQueryHandler queryRef={queryRef} />
      ) : null}
    </Suspense>
  );
};

const AnonymousMemberRedirectQueryHandler = ({
  queryRef,
}: {
  queryRef: PreloadedQuery<AnonymousMemberRedirectQuery>;
}) => {
  const data = usePreloadedQuery(AnonymousMemberRedirectQueryString, queryRef);
  const clubSlugToTarget =
    data.session?.currentClientProfile?.member?.club.facilities?.edges?.find(
      (e) => {
        return (
          e?.node?.publicBookingPermissionSet?.isFacilityVisible &&
          e?.node?.publicBookingPermissionSet?.isFacilityBookable
        );
      }
    )?.node?.slug || null;
  /**
   * If we don't have a member ID, that means we are an anonymous
   * user. In this case -- we should not see the home screen, and should instead
   * get sent to the booking's screen
   */
  const redirectNavigator =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const baseNavigator = useNavigation<NavigationProp<BaseNavigatorParamList>>();
  useLayoutEffect(() => {
    /**
     * If we have a club slug, send them to the agenda for today
     */
    if (clubSlugToTarget) {
      redirectNavigator.navigate("Agenda", {
        facilitySlug: clubSlugToTarget,
        date: "today",
      });
    } else {
      /**If we don't have a club slug then this is a private club so I don't know how you got here
       * guess we will just send you to the login screen
       */
      baseNavigator.reset({
        index: 0,
        routes: [{ name: "Unauthenticated" }],
      });
    }
  }, [baseNavigator, clubSlugToTarget, redirectNavigator]);
  return null;
};

export const AnonymousMemberRedirectQueryString = graphql`
  query AnonymousMemberRedirectQuery {
    session {
      currentClientProfile {
        member {
          club {
            facilities {
              edges {
                node {
                  slug
                  publicBookingPermissionSet {
                    id
                    isFacilityVisible
                    isFacilityBookable
                    areOtherBookingsVisible
                    areOtherBookingsJoinable
                    canBookOnBusySlot
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
